/**
 * aethereum join — connect to a room as a human participant.
 *
 * Opens the TUI and connects to a hive server over HTTP.
 * Events stream in via SSE, messages sent via POST /message.
 */

import { randomUUID } from "node:crypto";
import { createInterface } from "node:readline";
import { homedir } from "node:os";
import { randomName } from "../core/names.js";
import type { RoomEvent } from "../core/events.js";
import type { AuthorityLevel } from "../core/types.js";
import { formatTimestamp } from "../agent/prompts.js";
import { startTUI, type DisplayEvent } from "./tui.js";
import { extractToken } from "./auth.js";
import { AccountClient, OfflineError } from "../hive/account-client.js";
import { machineHandle } from "../hive/machine-handle.js";
import { wireProject } from "./init.js";

export interface JoinOptions {
  server: string;
  name?: string;
  guest?: boolean;
  /** Share URL to display before TUI starts (for host+join mode with --share). */
  shareUrl?: string;
  /** Skip TUI — stream events as JSON to stdout, read messages from stdin. */
  headless?: boolean;
}

const HOSTED_DEFAULT_BASE = "https://www.aethereum.dev";

/** A hosted JOIN CODE (short, e.g. 6 chars) vs a local room-server URL. A code has
 *  no scheme and no slash; anything with `://` or `/` is treated as a URL. */
function looksLikeJoinCode(input: string): boolean {
  const s = input.trim();
  if (!s) return false;
  if (s.includes("://") || s.includes("/") || s.includes(".") || s.includes(":")) return false;
  return /^[A-Za-z0-9_-]{4,16}$/.test(s);
}

export interface JoinCodeOptions {
  url?: string;
  home?: string;
  cwd?: string;
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
}

/** Redeem a hosted join code: call /api/join, store the room in this project's
 *  config, wire this project's agents (same writers as `init`), and print the
 *  connect confirmation. Fails soft with clear errors. */
export async function joinByCode(code: string, options: JoinCodeOptions = {}): Promise<void> {
  const home = options.home ?? homedir();
  const cwd = options.cwd ?? process.cwd();
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  const base = (options.url ?? env["AETHEREUM_SYNC_API_URL"] ?? HOSTED_DEFAULT_BASE)
    .replace(/\/$/, "")
    .replace(/^https:\/\/aethereum\.dev(?=$|\/)/, "https://www.aethereum.dev");

  const client = new AccountClient(base, options.fetchImpl ?? fetch);
  let result;
  try {
    result = await client.joinRoom(code.trim(), machineHandle());
  } catch (err) {
    if (err instanceof OfflineError) {
      console.error("Could not reach Aethereum (offline?). Try again when you are online.");
    } else {
      console.error(err instanceof Error ? err.message : String(err));
    }
    process.exit(1);
    return;
  }

  if (!result.token) {
    console.error("That join code did not return a room token. Check the code and retry.");
    process.exit(1);
    return;
  }

  console.log(`\n  Joining room with code ${code.trim()}...\n`);
  const wired = wireProject(cwd, home, base, result.token, env, {
    roomId: result.roomId,
    anon: true,
    joinCode: code.trim(),
  });
  for (const line of wired.summary) console.log(line);

  const viewUrl = result.viewUrl || `${base}/dashboard`;
  console.log(`
  ─────────────────────────────────────────────────────────────────────────
  You're in${result.handle ? ` as "${result.handle}"` : ""}. This project is now connected to the shared room.

  Watch it live:        ${viewUrl}
  Now just use your agent:  open Claude Code, Cursor, or Codex in this folder and
                            use it as you normally do, it shares the room automatically.
  ─────────────────────────────────────────────────────────────────────────`);
}

export async function join(options: JoinOptions): Promise<void> {
  // A short hosted join code (no scheme/slash) means "redeem + wire this project",
  // not "open the local-server TUI". Route it to the hosted join path.
  if (looksLikeJoinCode(options.server)) {
    await joinByCode(options.server);
    return;
  }
  // Extract token from URL if present
  const token = extractToken(options.server);
  // Strip query params to get clean server URL
  let serverUrl: string;
  try {
    const parsed = new URL(options.server);
    parsed.search = "";
    serverUrl = parsed.toString().replace(/\/$/, "");
  } catch {
    serverUrl = options.server.replace(/\/$/, "");
  }

  const name = options.name ?? randomName();
  const isGuest = options.guest ?? false;

  // ── Register with server ────────────────────────────────────────────────

  let sessionToken: string;
  let participantId: string;
  let roomName: string;
  let authority: AuthorityLevel;
  let participants: Array<{ id: string; name: string; type: string; authority?: string }>;

  try {
    const joinBody: Record<string, unknown> = {};
    if (token) {
      joinBody.token = token;
      joinBody.type = "human";
      joinBody.name = name;
    } else if (isGuest) {
      joinBody.type = "guest";
    } else {
      joinBody.type = "human";
      joinBody.name = name;
    }

    const res = await fetch(`${serverUrl}/join`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(joinBody),
    });

    if (!res.ok) {
      const err = await res.text();
      console.error(`Failed to join: ${err}`);
      process.exit(1);
    }

    const data = await res.json() as Record<string, unknown>;
    sessionToken = String(data.sessionToken ?? "");
    participantId = String(data.participantId);
    roomName = String(data.roomName);
    authority = (data.authority as AuthorityLevel) ?? "member";
    participants = (data.participants as Array<{ id: string; name: string; type: string; authority?: string }>) ?? [];
  } catch {
    console.error(`Cannot reach the aethereum server at ${serverUrl}. Is it running?`);
    process.exit(1);
  }

  // ── Disconnect helper ───────────────────────────────────────────────────

  let disconnected = false;
  let cleanupStream: (() => void) | null = null;
  const disconnect = async () => {
    if (disconnected) return;
    disconnected = true;
    cleanupStream?.();
    try {
      await fetch(`${serverUrl}/disconnect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: sessionToken }),
      });
    } catch {
      // Server may be down
    }
  };

  // ── Headless mode — skip TUI, stream events as JSON, read messages from stdin ──

  if (options.headless) {
    const sseController = new AbortController();
    const cleanup = async () => {
      sseController.abort();
      await disconnect();
    };

    process.on("SIGINT", async () => { await cleanup(); process.exit(0); });
    process.on("SIGTERM", async () => { await cleanup(); process.exit(0); });

    // Read messages from stdin and send them
    const rl = createInterface({ input: process.stdin, terminal: false });
    rl.on("line", async (line) => {
      const content = line.trim();
      if (!content || authority === "guest") return;
      try {
        await fetch(`${serverUrl}/message`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: sessionToken, content }),
        });
      } catch { /* server may be down */ }
    });

    // Stream events from SSE and write as JSON lines to stdout
    try {
      const res = await fetch(`${serverUrl}/events`, {
        method: "POST",
        headers: { Accept: "text/event-stream", Authorization: `Bearer ${sessionToken}` },
        signal: sseController.signal,
      });

      if (!res.ok || !res.body) {
        process.stderr.write("Failed to connect event stream\n");
        await cleanup();
        process.exit(1);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop()!;
        for (const part of parts) {
          const dataLine = part.split("\n").find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          try {
            const event = JSON.parse(dataLine.slice(6));
            process.stdout.write(JSON.stringify(event) + "\n");
          } catch { /* malformed */ }
        }
      }
    } catch {
      // Stream ended or aborted
    }

    if (!disconnected) { await cleanup(); }
    process.exit(0);
  }

  // ── Start TUI ───────────────────────────────────────────────────────────

  const isReadOnly = authority === "guest" || isGuest;

  // ── Slash command helper ──────────────────────────────────────────────

  function systemEvent(content: string): void {
    tui.push({
      id: randomUUID(),
      ts: formatTimestamp(new Date()),
      kind: "system",
      content,
    });
  }

  async function handleSlashCommand(input: string): Promise<void> {
    const parts = input.slice(1).split(/\s+/);
    const cmd = parts[0]?.toLowerCase();
    const args = parts.slice(1);

    switch (cmd) {
      // ── /who ──────────────────────────────────────────────────────
      case "who": {
        try {
          const res = await fetch(`${serverUrl}/participants?token=${sessionToken}`);
          if (!res.ok) { systemEvent("Failed to get participant list."); return; }
          const data = (await res.json()) as { participants: Array<{ id: string; name: string; type: string; authority?: string }> };
          const lines = data.participants.map((p) => {
            const auth = p.authority ?? "member";
            return `  ${p.type === "agent" ? "agent" : "human"} ${p.name} (${auth})`;
          });
          systemEvent(`Participants:\n${lines.join("\n")}`);
        } catch {
          systemEvent("Failed to reach server.");
        }
        return;
      }

      // ── /leave ────────────────────────────────────────────────────
      case "leave": {
        await disconnect();
        tui.stop();
        process.exit(0);
        return;
      }

      // ── /kick <name> (admin only) ─────────────────────────────────
      case "kick": {
        if (authority !== "admin") { systemEvent("Only admins can kick."); return; }
        const targetName = args[0];
        if (!targetName) { systemEvent("Usage: /kick <name>"); return; }

        // Look up participant by name
        try {
          const res = await fetch(`${serverUrl}/participants?token=${sessionToken}`);
          if (!res.ok) { systemEvent("Failed to get participant list."); return; }
          const data = (await res.json()) as { participants: Array<{ id: string; name: string }> };
          const target = data.participants.find((p) => p.name.toLowerCase() === targetName.toLowerCase());
          if (!target) { systemEvent(`Participant "${targetName}" not found.`); return; }

          const kickRes = await fetch(`${serverUrl}/kick`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: sessionToken, participantId: target.id }),
          });
          if (!kickRes.ok) { systemEvent(`Failed to kick: ${await kickRes.text()}`); return; }
          systemEvent(`Kicked ${targetName}.`);
        } catch {
          systemEvent("Failed to reach server.");
        }
        return;
      }

      // ── /mute <name> (admin only) — demote to observer ────────────
      case "mute": {
        if (authority !== "admin") { systemEvent("Only admins can mute."); return; }
        const targetName = args[0];
        if (!targetName) { systemEvent("Usage: /mute <name>"); return; }

        try {
          const res = await fetch(`${serverUrl}/participants?token=${sessionToken}`);
          if (!res.ok) { systemEvent("Failed to get participant list."); return; }
          const data = (await res.json()) as { participants: Array<{ id: string; name: string }> };
          const target = data.participants.find((p) => p.name.toLowerCase() === targetName.toLowerCase());
          if (!target) { systemEvent(`Participant "${targetName}" not found.`); return; }

          const authRes = await fetch(`${serverUrl}/set-authority`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: sessionToken, participantId: target.id, authority: "guest" }),
          });
          if (!authRes.ok) { systemEvent(`Failed to mute: ${await authRes.text()}`); return; }
          systemEvent(`Muted ${targetName} (guest).`);
        } catch {
          systemEvent("Failed to reach server.");
        }
        return;
      }

      // ── /unmute <name> (admin only) — restore to participant ──────
      case "unmute": {
        if (authority !== "admin") { systemEvent("Only admins can unmute."); return; }
        const targetName = args[0];
        if (!targetName) { systemEvent("Usage: /unmute <name>"); return; }

        try {
          const res = await fetch(`${serverUrl}/participants?token=${sessionToken}`);
          if (!res.ok) { systemEvent("Failed to get participant list."); return; }
          const data = (await res.json()) as { participants: Array<{ id: string; name: string }> };
          const target = data.participants.find((p) => p.name.toLowerCase() === targetName.toLowerCase());
          if (!target) { systemEvent(`Participant "${targetName}" not found.`); return; }

          const authRes = await fetch(`${serverUrl}/set-authority`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: sessionToken, participantId: target.id, authority: "member" }),
          });
          if (!authRes.ok) { systemEvent(`Failed to unmute: ${await authRes.text()}`); return; }
          systemEvent(`Unmuted ${targetName} (member).`);
        } catch {
          systemEvent("Failed to reach server.");
        }
        return;
      }

      // ── /setmode <name> <mode> (admin only) ───────────────────────
      case "setmode": {
        if (authority !== "admin") { systemEvent("Only admins can set modes."); return; }
        const targetName = args[0];
        const mode = args[1];
        if (!targetName || !mode) { systemEvent("Usage: /setmode <name> <mode>"); return; }

        try {
          const res = await fetch(`${serverUrl}/participants?token=${sessionToken}`);
          if (!res.ok) { systemEvent("Failed to get participant list."); return; }
          const data = (await res.json()) as { participants: Array<{ id: string; name: string }> };
          const target = data.participants.find((p) => p.name.toLowerCase() === targetName.toLowerCase());
          if (!target) { systemEvent(`Participant "${targetName}" not found.`); return; }

          const modeRes = await fetch(`${serverUrl}/set-mode`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: sessionToken, participantId: target.id, mode }),
          });
          if (!modeRes.ok) { systemEvent(`Failed to set mode: ${await modeRes.text()}`); return; }
          systemEvent(`Set ${targetName} to ${mode}.`);
        } catch {
          systemEvent("Failed to reach server.");
        }
        return;
      }

      // ── /share [--as <tier>] ──────────────────────────────────────
      case "share": {
        if (authority === "guest") { systemEvent("Guests cannot create share links."); return; }

        let targetAuthority: string | undefined;
        if (args[0] === "--as" && args[1]) {
          targetAuthority = args[1];
        }

        try {
          const body: Record<string, unknown> = { token: sessionToken };
          if (targetAuthority) body.authority = targetAuthority;

          const res = await fetch(`${serverUrl}/share`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          if (!res.ok) { systemEvent(`Failed: ${await res.text()}`); return; }
          const data = (await res.json()) as { links: Record<string, string> };
          const lines = Object.entries(data.links).map(([tier, url]) =>
            `  ${tier}: aethereum join ${url}`
          );
          systemEvent(`Share links:\n${lines.join("\n")}`);
        } catch {
          systemEvent("Failed to reach server.");
        }
        return;
      }

      default:
        systemEvent(`Unknown command: /${cmd}`);
    }
  }

  // Print share info via console.log BEFORE Ink renders. This lands in the
  // terminal buffer above Ink's render area — plain text, no ANSI codes,
  // fully selectable. Each line is a complete copyable command.
  if (options.shareUrl) {
    console.log();
    console.log(`  Invite a friend:       npx aethereum join "${options.shareUrl}"`);
    console.log(`  Connect Claude Code:   npx aethereum run claude --name MyClaude  →  then tell agent to join: ${options.shareUrl}`);
    console.log(`  Connect Codex:         npx aethereum run codex --name MyCodex   →  then tell agent to join: ${options.shareUrl}`);
    console.log();
  }

  const tui = startTUI({
    roomName,
    readOnly: isReadOnly,
    isAdmin: authority === "admin",
    onSend: isReadOnly ? undefined : async (content: string) => {
      // Intercept slash commands
      if (content.startsWith("/")) {
        await handleSlashCommand(content);
        return;
      }

      try {
        await fetch(`${serverUrl}/message`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: sessionToken, content }),
        });
      } catch {
        // Server may be down — silently fail
      }
    },
    onCtrlC: async () => {
      await disconnect();
      tui.stop();
      process.exit(0);
    },
  });

  // Set initial agent names + participant names
  const agentNames = participants
    .filter((p) => p.type === "agent")
    .map((p) => p.name);
  if (agentNames.length > 0) {
    tui.setAgentNames(agentNames);
  }
  const participantNames = new Set(
    participants.filter((p) => p.id !== participantId).map((p) => p.name),
  );
  tui.setParticipants([...participantNames]);

  // ── Connect SSE event stream ────────────────────────────────────────────
  // ⚠️  MUST use POST — DO NOT change to GET.
  // Cloudflare Quick Tunnels buffer GET streaming responses and only flush
  // when the connection closes. POST streams in real-time. (cloudflared#1449)
  // https://github.com/cloudflare/cloudflared/issues/1449

  {
    const participantTypes = new Map<string, "human" | "agent">();
    for (const p of participants) {
      participantTypes.set(p.id, p.type as "human" | "agent");
    }
    const currentAgents = new Set(agentNames);
    let sseController: AbortController | null = null;

    cleanupStream = () => {
      if (sseController) { sseController.abort(); sseController = null; }
    };

    const connectSSE = async () => {
      sseController = new AbortController();

      try {
        const res = await fetch(`${serverUrl}/events`, {
          method: "POST",
          headers: {
            Accept: "text/event-stream",
            Authorization: `Bearer ${sessionToken}`,
          },
          signal: sseController.signal,
        });

        if (!res.ok || !res.body) {
          console.error("Failed to connect event stream");
          await disconnect();
          process.exit(1);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop()!;

          for (const part of parts) {
            const dataLine = part.split("\n").find((l) => l.startsWith("data: "));
            if (!dataLine) continue;

            try {
              const event = JSON.parse(dataLine.slice(6)) as RoomEvent & { _replyToName?: string };

              if (event.type === "ParticipantJoined") {
                participantTypes.set(event.participant.id, event.participant.type);
              }

              const displayEvent = toDisplayEvent(event, participantId, participantTypes);
              if (displayEvent) {
                tui.push(displayEvent);
              }

              if (event.type === "ParticipantJoined") {
                if (event.participant.type === "agent") {
                  currentAgents.add(event.participant.name);
                  tui.setAgentNames([...currentAgents]);
                }
                if (event.participant.id !== participantId) {
                  participantNames.add(event.participant.name);
                  tui.setParticipants([...participantNames]);
                }
              }
              if (event.type === "ParticipantLeft") {
                if (event.participant.type === "agent") {
                  currentAgents.delete(event.participant.name);
                  tui.setAgentNames([...currentAgents]);
                }
                participantTypes.delete(event.participant.id);
                participantNames.delete(event.participant.name);
                tui.setParticipants([...participantNames]);
              }
            } catch {
              // Malformed event — skip
            }
          }
        }

        // Stream ended — server closed connection
        if (!disconnected) {
          tui.stop();
          console.log("\nServer disconnected.");
          process.exit(0);
        }
      } catch {
        if (!disconnected) {
          tui.stop();
          console.log("\nServer disconnected.");
          process.exit(0);
        }
      }
    };

    connectSSE();
  }

  // ── Graceful shutdown ───────────────────────────────────────────────────

  process.on("SIGINT", async () => {
    await disconnect();
    tui.stop();
    process.exit(0);
  });
  process.on("SIGTERM", async () => {
    await disconnect();
    tui.stop();
    process.exit(0);
  });
}

// ── RoomEvent → DisplayEvent conversion ───────────────────────────────────────

function toDisplayEvent(
  event: RoomEvent & { _replyToName?: string },
  selfId: string,
  participantTypes: Map<string, "human" | "agent">,
): DisplayEvent | null {
  const ts = formatTimestamp(new Date(event.timestamp));

  switch (event.type) {
    case "MessageSent": {
      const msg = event.message;
      const senderType = participantTypes.get(msg.sender_id) ?? "human";
      return {
        id: msg.id,
        ts,
        kind: "message",
        senderName: msg.sender_name,
        senderType,
        isSelf: msg.sender_id === selfId,
        content: msg.content,
        replyToName: event._replyToName ?? undefined,
      };
    }
    case "ParticipantJoined":
      return {
        id: randomUUID(),
        ts,
        kind: "join",
        name: event.participant.name,
        participantType: event.participant.type,
      };
    case "ParticipantLeft":
      return {
        id: randomUUID(),
        ts,
        kind: "leave",
        name: event.participant.name,
        participantType: event.participant.type,
      };
    case "ParticipantKicked":
      return {
        id: randomUUID(),
        ts,
        kind: "system",
        content: `${event.participant.name} was kicked`,
      };
    case "AuthorityChanged": {
      const name = event.participant.name;
      if (event.new_authority === "guest") {
        return { id: randomUUID(), ts, kind: "system", content: `${name} was muted` };
      }
      if (event.new_authority === "member") {
        return { id: randomUUID(), ts, kind: "system", content: `${name} was unmuted` };
      }
      return { id: randomUUID(), ts, kind: "system", content: `${name} → ${event.new_authority}` };
    }
    case "Activity":
      if (event.action === "mode_changed") {
        return {
          id: randomUUID(),
          ts,
          kind: "mode",
          mode: String((event.detail as Record<string, unknown>)?.mode ?? ""),
        };
      }
      return null;
    default:
      return null;
  }
}
